import { DateTime } from 'luxon';

/**
 * Offset-aware API dateTime string → browser-local `DateTime`.
 * Use when consuming measurement/event timestamps that include a zone offset.
 */
export const fromApiDateTime = (value: string): DateTime => DateTime.fromISO(value).toLocal();

/** Zone the API buckets daily and weekly data in: each bucket starts at midnight in this zone. */
export const API_BUCKET_ZONE = 'Europe/Oslo';

/**
 * Day-level API window bound: the calendar date of `dateTime` in the browser zone, sent as the start
 * of that day in Oslo, with its offset. The API reads a bare date as UTC midnight, which is 22:00
 * or 23:00 the evening before an Oslo day starts, so it would skip the first day and include today.
 */
export const toApiDate = (dateTime: DateTime): string => {
  const { year, month, day } = dateTime.isValid ? dateTime.toLocal() : DateTime.now();
  return DateTime.fromObject({ year, month, day }, { zone: API_BUCKET_ZONE }).toISO()!;
};

/**
 * The API calendar day of an instant, as midnight of that calendar date in the browser zone.
 * Charts and labels work in the browser zone, so an API day is carried as a local midnight with the
 * same calendar date. Use it to anchor day-level windows ("today") to API days.
 */
export const toApiCalendarDay = (dateTime: DateTime): DateTime => {
  const apiDay = dateTime.setZone(API_BUCKET_ZONE);
  if (!apiDay.isValid) return apiDay;
  return DateTime.fromObject({ year: apiDay.year, month: apiDay.month, day: apiDay.day });
};

/**
 * Daily or weekly API bucket timestamp → the calendar day it belongs to, as a local midnight.
 * Use instead of `fromApiDateTime` whenever a bucket is shown or grouped by day or week.
 */
export const fromApiBucketDay = (value: string): DateTime =>
  toApiCalendarDay(DateTime.fromISO(value, { setZone: true }));

/**
 * Browser-local `DateTime` → ISO string **with** offset for timed API request fields.
 * Prefer this over `.toUTC().toISO()` or `new Date().toISOString()`.
 */
export const toApiDateTime = (dateTime: DateTime): string =>
  dateTime.toLocal().toISO() ?? DateTime.now().toLocal().toISO()!;
